import styled from "styled-components";

export const FormCard = styled.form`
  width: 100%;
  max-width: 725px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(7)};
  border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.light.medium};
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(6)};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

export const FieldGroup = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const FieldLabel = styled.span`
  color: ${({ theme }) => theme.colors.mediumGray};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

/** Texto de erro em vermelho escuro: o vermelho da marca dá só 3:1 sobre branco. */
export const FieldError = styled.span.attrs({ role: "alert" })`
  color: #c9372c;
  font-size: 0.8rem;
`;

export const FieldHint = styled.span`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  padding-left: ${({ theme }) => theme.spacing(4)};
`;

/** Container relativo que ancora o dropdown de aeroportos. */
export const AirportField = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.white};
`;

export const ChannelList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

export const ChannelOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const FormTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.deepDarkBlue};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

export const FormHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const StatusBadge = styled.span<{ $paused?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $paused }) => ($paused ? "#FFF1D6" : "#E2F5EB")};
  color: ${({ $paused }) => ($paused ? "#7A4B00" : "#146B45")};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  white-space: nowrap;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $paused }) => ($paused ? "#E08A00" : "#1FA463")};
  }
`;

export const FormFootnote = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.mediumGray};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-align: center;
`;

/** Rótulo só para leitores de tela: o placeholder já diz o mesmo na tela. */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Mensagem de erro do campo, sobreposta no vão logo abaixo dele e sem ocupar
 * altura: o card não cresce nem se mexe quando o erro aparece ou some.
 * Exige um ancestral com `position: relative`.
 */
export const ErrorSlot = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  font-size: 0.8rem;
  line-height: 1.25;
  pointer-events: none;
`;

export const DatesRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: ${({ theme }) => theme.spacing(4)};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1.25)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.mediumBlue};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme }) => theme.colors.mediumBlue};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.darkBlue};
    border-color: ${({ theme }) => theme.colors.darkBlue};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing(2)};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    & > button {
      width: 100%;
    }
  }
`;

/* ========== Bar variant styles ========== */

/**
 * Container wrapper: flex row on desktop, column on mobile
 * This is the form element itself
 */
export const BarContainer = styled.form`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
    align-items: stretch;
  }
`;

/**
 * Content wrapper for bar, checkbox, errors (flex: 1 on desktop)
 */
export const BarContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
  }
`;

/**
 * Linha de campos da barra (desktop: horizontal row; mobile: vertical column)
 */
export const BarFieldsRow = styled.div`
  display: flex;
  align-items: stretch;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.light.small};
  height: ${({ theme }) => theme.spacing(11.25)}; /* ~4.5rem desktop */
  overflow: visible;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    height: auto;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
  }
`;

/**
 * Campo individual dentro da barra
 * Desktop: inline with height 100%, right border, flex
 * Mobile: full width, fixed height, bottom border
 */
export const BarField = styled.div<{
  $disabled?: boolean;
  $hasError?: boolean;
  /** Valor de `flex` no desktop; no mobile o campo usa altura fixa. */
  $flex?: string;
}>`
  display: flex;
  align-items: center;
  position: relative;
  flex: ${({ $flex }) => $flex ?? "1"};
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: 0 ${({ theme }) => theme.spacing(2)};
  min-width: 0;
  white-space: nowrap;
  height: 100%;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  border-right: 1px solid ${({ theme }) => theme.colors.dividerGray};

  &:last-of-type {
    border-right: none;
  }

  /* Input ou campo de busca */
  & input:not([type="checkbox"]) {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    font-family: inherit;
    font-size: ${({ theme }) => theme.fontSizes.md};
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &::placeholder {
      color: ${({ theme }) => theme.colors.darkGray};
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.darkGray};
    }
  }

  /* Campo obrigatório pendente */
  ${({ $hasError, theme }) =>
    $hasError &&
    `
    box-shadow: inset 0 -3px 0 ${theme.colors.red};

    & input::placeholder {
      color: ${theme.colors.red};
    }
  `}

  /* Mobile: vertical stacked rows */
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    flex: 0 0 auto;
    height: ${({ theme }) => theme.spacing(8.75)}; /* ~3.5rem */
    min-height: ${({ theme }) => theme.spacing(8.75)};
    padding: 0 ${({ theme }) => theme.spacing(3)};
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.dividerGray};
    white-space: normal;

    &:last-of-type {
      border-bottom: none;
    }
  }
`;

/**
 * Ícone com cor cyan para os campos da barra
 * Wraps the Icon atom para aplicar cor via SVG fill=currentColor
 */
export const BarIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.cyan};

  & div,
  & svg {
    color: inherit;
  }
`;

/**
 * Chevron (⌄) à direita de um campo
 */
export const BarChevron = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  white-space: nowrap;
`;

/**
 * Botão trigger do popover de notificação
 */
export const BarNotifyTrigger = styled.button`
  flex: 1;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0;
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  white-space: nowrap;
  text-align: left;
  min-width: 0;
  overflow: hidden;

  &:hover {
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus {
    outline: none;
  }
`;

export const BarNotifyLabel = styled.span`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: ${({ theme }) => theme.fontSizes.md};
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const BarNotifyValue = styled.span`
  color: ${({ theme }) => theme.colors.ink};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  white-space: nowrap;
  flex-shrink: 0;
`;

/**
 * Popover do canal de notificação
 */
export const BarNotifyPopover = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing(1)});
  left: 0;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.light.medium};
  z-index: 1000;
  min-width: ${({ theme }) => theme.spacing(56)};
  white-space: normal;
  overflow: visible;
`;

export const NotifyPopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};

  /* Opções simples, sem o contorno de card da variante vertical */
  & ${ChannelOption} {
    padding: 0;
    border: none;
    border-radius: 0;
  }
`;

/**
 * Campo de preço do popover: prefixo "R$" + input com linha inferior
 */
export const PricePopoverField = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.darkGray};
`;

export const PricePopoverPrefix = styled.span`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

export const PricePopoverInput = styled.input`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  outline: none;
`;

/**
 * Título do popover de canal de notificação
 */
export const NotifyPopoverTitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  max-width: ${({ theme }) => theme.spacing(50)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.25;
`;

/**
 * Botão amarelo de confirmação do popover (canto inferior direito)
 */
export const NotifyConfirmButton = styled.button`
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing(7)};
  height: ${({ theme }) => theme.spacing(6)};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.brightYellow};
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ink};
    outline-offset: 2px;
  }
`;

/**
 * Linha de checkbox "Datas abertas" (branco, abaixo da barra)
 */
export const DatesCheckbox = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)} 0;

  /* Label dentro do checkbox */
  & label {
    color: ${({ theme }) => theme.colors.white};
    font-size: ${({ theme }) => theme.fontSizes.md};
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    cursor: pointer;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0;
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

/**
 * Erros inline da barra, visíveis no hero laranja
 */
export const BarErrors = styled.div`
  align-self: flex-start;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.red};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  line-height: 1.4;
`;

/**
 * Botão de envio na variante bar
 * Desktop: ao lado da barra, mesma altura, flex-shrink 0
 * Mobile: full width abaixo do checkbox
 */
export const BarSubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0 ${({ theme }) => theme.spacing(4)};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background-color: ${({ theme }) => theme.colors.brightYellow};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-family: ${({ theme }) => theme.fonts.primary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: ${({ theme }) => theme.shadows.light.small};
  flex-shrink: 0;
  white-space: nowrap;
  height: ${({ theme }) => theme.spacing(11.25)}; /* ~4.5rem, match bar height */

  &:hover:not(:disabled) {
    background-color: #ffd700;
    box-shadow: ${({ theme }) => theme.shadows.light.medium};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    height: auto;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

/**
 * Slot dos campos de busca de aeroporto (origem/destino) na barra.
 * Os containers aninhados precisam poder encolher para o texto truncar, e o
 * botão de limpar fica no fluxo do flex, sem vazar para o campo vizinho.
 * Escopo restrito a este slot para não afetar o calendário.
 */
export const BarSearchSlot = styled.div`
  flex: 1;
  min-width: 0;

  & div {
    min-width: 0;
  }

  & input {
    min-width: 0;
    width: 100%;
  }

  & button[aria-label="Clear input"] {
    position: static;
    right: auto;
    flex-shrink: 0;
    margin-left: ${({ theme }) => theme.spacing(1)};
    padding: 0;
  }
`;
